import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private siteKey = environment.RECAPTCHA.SITE_KEY;
  private scriptLoaded = false;
  private scriptLoading = false;

  constructor() {
  }

  /**
   * Dynamically load reCAPTCHA Enterprise script with environment site key
   */
  private loadRecaptchaScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (this.scriptLoaded || typeof window.grecaptcha !== 'undefined') {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      // Check if script is currently loading
      if (this.scriptLoading) {
        // Wait for the existing load to complete
        this.waitForRecaptcha().then(resolve).catch(reject);
        return;
      }

      this.scriptLoading = true;

      // Create script element
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.scriptLoaded = true;
        this.scriptLoading = false;
        resolve();
      };

      script.onerror = (error) => {
        this.scriptLoading = false;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      // Add script to document head
      document.head.appendChild(script);
    });
  }

  async initializeRecaptcha(): Promise<void> {
    await this.loadRecaptchaScript();
  }

  /**
   * Execute reCAPTCHA Enterprise and get token
   * @param action The action name for reCAPTCHA scoring
   * @returns Promise that resolves to reCAPTCHA token
   */
  async executeRecaptcha(action: string): Promise<string> {
    // Ensure script is loaded first
    await this.loadRecaptchaScript();
    
    return new Promise((resolve, reject) => {
      if (typeof window.grecaptcha === 'undefined') {
        reject(new Error('reCAPTCHA not loaded'));
        return;
      }

      // Wait for reCAPTCHA to be ready
      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise.execute(this.siteKey, { action })
          .then((token: string) => {
            resolve(token);
          })
          .catch((error: any) => {
            reject(error);
          });
      });
    });
  }

  /**
   * Check if reCAPTCHA is available
   * @returns boolean indicating if reCAPTCHA is loaded
   */
  isRecaptchaLoaded(): boolean {
    return this.scriptLoaded && 
           typeof window.grecaptcha !== 'undefined' && 
           typeof window.grecaptcha.enterprise !== 'undefined';
  }

  /**
   * Wait for reCAPTCHA to be loaded
   * @param timeout Maximum wait time in milliseconds (default: 10000)
   * @returns Promise that resolves when reCAPTCHA is ready
   */
  waitForRecaptcha(timeout: number = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      
      const checkRecaptcha = () => {
        if (this.isRecaptchaLoaded()) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error('reCAPTCHA loading timeout'));
        } else {
          setTimeout(checkRecaptcha, 100);
        }
      };
      
      checkRecaptcha();
    });
  }

  /**
   * Get the current site key from environment
   * @returns The reCAPTCHA site key
   */
  getSiteKey(): string {
    return this.siteKey;
  }

  cleanupRecaptcha(): void {
    const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
    if (existingScript) {
      existingScript.remove();
    }    
    this.scriptLoaded = false;
    this.scriptLoading = false;
    if (typeof window !== 'undefined') {
      delete (window as any).grecaptcha;
    }
  }


  hideRecaptchaBadge(): void {
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) {
      (badge as HTMLElement).style.visibility = 'hidden';
    }
  }
  showRecaptchaBadge(): void {
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) {
      (badge as HTMLElement).style.visibility = 'visible';
    }
  }
  /**
   * Get current environment info
   * @returns Environment and configuration info
   */
  getEnvironmentInfo() {
    return {
      production: environment.production,
      siteKey: this.siteKey,
      scriptLoaded: this.scriptLoaded,
      scriptLoading: this.scriptLoading
    };
  }
}
